/**
 * Moderator hide/unhide for any review (Backlog 060 follow-up), shared by the Stories → Reviews tab and the user
 * page's Reviews section.
 *
 * A hidden review leaves every public list and every average; its author still sees it, with the reason, so they
 * understand why it vanished. Hiding needs a reason (it is shown to the author and recorded on their audit
 * history); unhiding takes an optional one. Admins and Moderators may both do it.
 */

import { Chip, IconButton, Tooltip } from '@mui/material'
import { Visibility, VisibilityOff } from '@mui/icons-material'
import { userDataApi } from '@api/endpoints/userData'
import type { AdminUserDataChangeResultDto } from '@api/types/userData'
import { UserDataActionDialog } from './UserDataActionDialog'

export interface HideableReview {
  id: string
  isHidden: boolean
  hiddenReason?: string | null
}

/** The "Hidden" chip, with the moderator's reason on hover. */
export function HiddenReviewChip({ review }: { review: HideableReview }) {
  if (!review.isHidden) return null
  return (
    <Tooltip title={review.hiddenReason ? `Hidden: ${review.hiddenReason}` : 'Hidden by a moderator'}>
      <Chip size="small" color="error" variant="outlined" label="Hidden" />
    </Tooltip>
  )
}

/** The row action: Hide on a visible review, Unhide on a hidden one. */
export function ReviewHideButton({ review, onClick }: { review: HideableReview; onClick: () => void }) {
  const label = review.isHidden ? 'Unhide review' : 'Hide review'
  return (
    <Tooltip title={label}>
      <IconButton size="small" aria-label={label} onClick={onClick}>
        {review.isHidden ? <Visibility fontSize="small" /> : <VisibilityOff fontSize="small" />}
      </IconButton>
    </Tooltip>
  )
}

export interface ReviewHideDialogProps {
  review: HideableReview
  /** "Review of X by Y", for the dialog's message. */
  description: string
  pending: boolean
  error: string | null
  submit: (call: () => Promise<AdminUserDataChangeResultDto>, onDone?: () => void) => void
  onDone: () => void
  onClose: () => void
}

/** Mount only while needed, like every UserDataActionDialog. */
export function ReviewHideDialog({ review, description, pending, error, submit, onDone, onClose }: ReviewHideDialogProps) {
  return review.isHidden ? (
    <UserDataActionDialog
      title="Unhide review?"
      message={`${description}. It goes back into the public lists and the averages.${
        review.hiddenReason ? ` Hidden because: ${review.hiddenReason}` : ''
      }`}
      confirmLabel="Unhide review"
      reasonOptional
      pending={pending}
      error={error}
      onSubmit={(reason) => submit(() => userDataApi.unhideRating(review.id, reason), onDone)}
      onClose={onClose}
    />
  ) : (
    <UserDataActionDialog
      title="Hide review?"
      message={`${description}. It leaves every public list and the averages; the author still sees it, with your reason.`}
      confirmLabel="Hide review"
      pending={pending}
      error={error}
      onSubmit={(reason) => submit(() => userDataApi.hideRating(review.id, reason), onDone)}
      onClose={onClose}
    />
  )
}
